import React from 'react'
import { ApexOptions } from 'apexcharts'
import Chart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import { NoData } from '@/ui/shared'
import { formatPercent } from '@/utils/format'

interface DonutProps {
  title: string
  labels?: string[]
  values?: number[]
  amounts?: string[]
  totalAmount?: string
}

const DonutComponent: React.FC<DonutProps> = ({
  title,
  values,
  labels,
  amounts,
  totalAmount
}) => {
  const { i18n } = useTranslation()
  const colors = ['#1B75EB', '#85B8FF', '#AEDCFF', '#003096', '#467bed']
  const valuesTrue = values && values.length > 0

  const chartOptions: ApexOptions = {
    labels: labels ?? [],
    chart: {
      animations: {
        speed: 400,
        animateGradually: {
          enabled: false
        }
      },
      fontFamily: 'inherit',
      foreColor: 'inherit',
      height: '100%',
      type: 'donut',
      sparkline: {
        enabled: true
      }
    },
    colors: valuesTrue ? colors.slice(0, values.length) : [],
    plotOptions: {
      pie: {
        customScale: 0.9,
        expandOnClick: false,
        donut: {
          size: '70%'
        }
      }
    },
    states: {
      hover: {
        filter: {
          type: 'none'
        }
      },
      active: {
        filter: {
          type: 'none'
        }
      }
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: 'dark',
      custom: ({ seriesIndex, w }) =>
        `<div class="flex items-center h-16 min-h-16 max-h-16 px-12">
            <div class="w-12 h-12 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
            <div class="ml-8 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
            <div class="ml-8 text-md font-bold leading-none">${formatPercent(
              w.config.series[seriesIndex],
              i18n.language
            )}</div>
        </div>`
    }
  }

  const valuesTotal = values?.reduce((acc, curr) => acc + curr, 0) ?? 0

  return (
    <div className="flex size-full flex-col items-center justify-between p-4">
      <p className="truncate text-lg font-medium leading-6">{title}</p>
      {values?.length && valuesTotal > 0 ? (
        <Chart
          options={chartOptions}
          series={values ?? []}
          type="donut"
          width="300"
        />
      ) : (
        <NoData />
      )}
      {values?.length && valuesTotal > 0 ? (
        <div className="w-full">
          {values &&
            values.map((value, i) => (
              <div key={i}>
                <div className="flex justify-between py-1">
                  <div className="flex items-center text-ellipsis">
                    <div
                      className="size-4 rounded-full"
                      style={{
                        backgroundColor: colors[i],
                        borderColor: colors[i]
                      }}
                    />
                    <p className="ml-4 truncate">{labels && labels[i]}</p>
                  </div>
                  <div className="flex w-40 justify-end">
                    <p className="ml-3 mr-1 w-8 text-left">
                      {formatPercent(value, i18n.language)}
                    </p>
                    {amounts && amounts.length > 0 ? (
                      <>
                        <p className="ml-auto flex shrink-0 rounded-lg border border-solid border-slate-300 px-2 text-xs leading-5">
                          {amounts[i]}
                        </p>
                      </>
                    ) : null}
                  </div>
                </div>
                {i === values.length - 1 ? null : <hr />}
              </div>
            ))}
          {amounts && amounts.length > 0 ? (
            <div className="flex justify-between border-t border-solid border-gray-200 py-1">
              <p className="ml-8 text-gray-400">Total</p>
              <div className="my-auto flex w-40 justify-end">
                <p className="ml-3 mr-1 w-8 text-left text-xs text-gray-400">
                  {formatPercent(100, i18n.language)}
                </p>
                <p className="ml-auto flex shrink-0 px-1 text-xs text-gray-400">
                  {totalAmount}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default DonutComponent
